import { useEffect } from 'react';

import { GrowthBook, useGrowthBook } from '@growthbook/growthbook-react';
import { SubscriptionPlan } from '@repo/domain/user';
import MemberSelect from '@repo/editor/src/components/PricingPlan/MemberSelect/MemberSelect';
import NewPricingPlan from '@repo/editor/src/components/PricingPlan/NewPricingPlan';
import { PricingPlanProps } from '@repo/editor/src/components/PricingPlan/types';
import { isPlanObject } from '@repo/editor/src/components/Subscription';
import PlanIntervalTabs from '@repo/editor/src/components/Subscription/SubscriptionModal/PlanIntervalTabs/PlanIntervalTabs';
import {
  getAddons,
  getPricingConfig,
} from '@repo/editor/src/global/pricingConfig';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@repo/editor/src/stores/planSelectionStore';
import {
  sharedConfigStoreSelector,
  useSharedConfigStore,
} from '@repo/editor/src/stores/sharedConfigStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { CreditLimits, PricingIntervals } from '@repo/editor/src/types/Pricing';
import { withEventScope } from '@repo/event-processor';
import {
  SubscriptionPlanType,
  type SubscriptionPrices,
} from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';

import { PlanContainersWrapper } from 'components/Subscription/PricingPlans/styles';
import teamsApi from 'global/teamsApi';
import { FlexColumn, FlexContainer } from 'styles/layouts';

import FreePlanMemberSelect from './FreePlanMemberSelect/FreePlanMemberSelect';
import { Headline, StackingContext, PlansContainer } from './styles';
import { ReactComponent as TeamIllustration } from './team.svg';
import { TeamPlansProps } from './types';

interface GetPricingPlanPropsParams {
  plan: SubscriptionPlanType;
  currentPlan: SubscriptionPlan | 'FREE' | null;
  quantity: number;
  growthBook?: GrowthBook;
  creditLimits?: CreditLimits;
  onSelectPlan?: TeamPlansProps['onSelectPlan'];
  yearly: boolean;
  subscriptionPrices: SubscriptionPrices;
  country: string | null;
}

const getPricingPlanProps = ({
  plan,
  currentPlan,
  quantity,
  growthBook,
  creditLimits,
  onSelectPlan,
  yearly,
  subscriptionPrices,
  country,
}: GetPricingPlanPropsParams): PricingPlanProps => {
  const {
    comparison,
    description,
    mainFeatures,
    otherFeatures,
    featuresLabel,
    price,
    title,
    buttonLabel,
  } = getPricingConfig({
    growthBook,
    creditLimits,
    subscriptionPrices,
    country,
  })[plan];

  const button: PricingPlanProps['button'] = {
    label: buttonLabel,
    onClick: () => onSelectPlan?.(plan, yearly),
    theme: secondaryTheme,
  };

  if (plan === SubscriptionPlanType.FREE && currentPlan === 'FREE') {
    button.label = 'Try for free';
  } else if (plan === currentPlan) {
    button.label = 'Change your plan';
  }

  if (plan === SubscriptionPlanType.EXPERT && currentPlan !== 'EXPERT') {
    button.theme = undefined;
  }

  return {
    comparison,
    description,
    mainFeatures,
    otherFeatures,
    featuresLabel,
    price,
    title,
    type: plan,
    yearly,
    quantity,
    button,
  };
};

const TeamPlans: React.FC<TeamPlansProps> = ({
  plansToShow = [
    SubscriptionPlanType.FREE,
    SubscriptionPlanType.PRO,
    SubscriptionPlanType.EXPERT,
  ],
  initialQuantity,
  onSelectPlan,
  className,
}) => {
  const growthBook = useGrowthBook();
  const creditLimits = useSharedConfigStore(
    sharedConfigStoreSelector.creditLimits
  );
  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );

  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const { setMembersQuantity, setYearly } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const user = useUserStore(userStoreSelector.user);
  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );
  const currentPlan = user?.plan || (user ? 'FREE' : null);
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const expertAddonPriceId = usePlanSelectionStore(
    planSelectionStoreSelector.expertAddonPriceId
  );

  const selectedExpertAddon = getAddons()?.find(
    (addon) => addon.stripeId === expertAddonPriceId
  );

  const onTabClick = (tab: string) => {
    setYearly(tab === PricingIntervals.Yearly);
  };

  useEffect(() => {
    const quantity =
      initialQuantity ??
      (currentSubscription?.plan && isPlanObject(currentSubscription.plan)
        ? currentSubscription.plan.quantity
        : null);

    if (quantity) {
      return setMembersQuantity(quantity);
    }

    teamsApi.listInvites(false).then((response) => {
      if (response.success) {
        const sentInvitationCount = response.results.length || 0;
        const teamMembers = user?.team?.memberCount || 1;
        setMembersQuantity(teamMembers + sentInvitationCount);
      }
    });
  }, [
    currentSubscription?.plan,
    initialQuantity,
    setMembersQuantity,
    user?.team?.memberCount,
  ]);

  return (
    <div className={className}>
      <FlexColumn alignItems="center">
        <TeamIllustration fill={themeStyle.varInkMain} />

        <Headline>Choose your team plan</Headline>
        <FlexContainer alignItems="center" gap="0.75rem">
          <PlanIntervalTabs
            activeTab={
              yearly ? PricingIntervals.Yearly : PricingIntervals.Monthly
            }
            onTabClick={onTabClick}
          />
        </FlexContainer>
        <Spacer height="14px" />

        <PlansContainer>
          <PlanContainersWrapper plans={plansToShow.length}>
            {plansToShow &&
              plansToShow.map((plan) => (
                <NewPricingPlan
                  key={plan}
                  memberSelect={
                    <StackingContext>
                      {plan === SubscriptionPlanType.FREE ? (
                        <FreePlanMemberSelect />
                      ) : (
                        <MemberSelect
                          selectedNumberOfMembers={quantity}
                          onSelect={(id): void => {
                            setMembersQuantity(id);
                          }}
                        />
                      )}
                    </StackingContext>
                  }
                  {...{
                    ...getPricingPlanProps({
                      plan,
                      growthBook,
                      currentPlan,
                      yearly,
                      quantity,
                      creditLimits,
                      onSelectPlan,
                      subscriptionPrices,
                      country,
                    }),
                    ...(plan === SubscriptionPlanType.EXPERT && {
                      selectedAddon: selectedExpertAddon,
                      label: 'RECOMMENDED',
                      showGenerationCreditsSlider: true,
                    }),
                  }}
                />
              ))}
          </PlanContainersWrapper>
        </PlansContainer>
      </FlexColumn>
    </div>
  );
};

export default withEventScope(TeamPlans, { feature: 'teamPlans' });
